import React, { useState, useEffect, useCallback } from 'react';
import { locationsService } from '../services/locations.service';
import { vendorRegistrationService } from '../services/vendorRegistration.service';
import { useAuth } from '../contexts/AuthContext';
import { Plus, ChevronDown, ChevronRight, Edit } from 'lucide-react';
import AddLocationRow from '../components/AddLocationRow';
import EditLocationRow from '../components/EditLocationRow';
import { formatDate } from '../utils/helpers';

const Locations = () => {
    const { user } = useAuth();
    const [locations, setLocations] = useState([]);
    const [vendor, setVendor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterActive, setFilterActive] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [showAddLocationRow, setShowAddLocationRow] = useState(false);
    const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
    const [locationToDeactivate, setLocationToDeactivate] = useState(null);
        const [editingLocationId, setEditingLocationId] = useState(null);
    const [newLocation, setNewLocation] = useState({
        location_address: {
            address: '',
            city: '',
            state: '',
            pincode: '',
            latitude: null,
            longitude: null
        },
        operating_hours: {
            monday: { open: '06:00', close: '18:00' },
            tuesday: { open: '06:00', close: '18:00' },
            wednesday: { open: '06:00', close: '18:00' },
            thursday: { open: '06:00', close: '18:00' },
            friday: { open: '06:00', close: '18:00' },
            saturday: { open: '07:00', close: '16:00' },
            sunday: { open: '08:00', close: '14:00' }
        },
        vendor_location_type: 'VENDOR_ONLY',
        manager_name: '',
        manager_mobile: '',
        manager_email: ''
    });

    const fetchLocations = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (filterActive !== null) {
                params.is_active = filterActive;
            }
            
            const response = await locationsService.getLocations(params);
            setLocations(response.locations || []);
            setVendor(response.vendor);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to fetch locations');
        } finally {
            setLoading(false);
        }
    }, [filterActive]);

    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);



    const handleStatusToggle = useCallback(async (locationId, currentStatus) => {
        try {
            await locationsService.updateLocationStatus(locationId, !currentStatus);
            // Refresh locations after status update
            fetchLocations();
        } catch (err) {
            setError(err.message || 'Failed to update location status');
        }
    }, [fetchLocations]);

    const toggleRowExpansion = useCallback((locationId) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(locationId)) {
            newExpandedRows.delete(locationId);
            // If we're collapsing a row that was being edited, cancel edit mode
            if (editingLocationId === locationId) {
                setEditingLocationId(null);
            }
        } else {
            newExpandedRows.add(locationId);
        }
        setExpandedRows(newExpandedRows);
    }, [expandedRows, editingLocationId, setExpandedRows, setEditingLocationId]);

    const handleAddLocation = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            
            // Validate required fields
            if (!newLocation.location_address.address || !newLocation.manager_name || !newLocation.manager_mobile) {
                setError('Please fill in all required fields (address, manager name, and mobile)');
                return;
            }

            // Get current user mobile number
            const userMobile = user?.mobile_number;
            
            if (!userMobile) {
                setError('User mobile number not found. Please log in again.');
                return;
            }

            // Prepare registration data in the same format as vendor registration
            const registrationData = {
                // Location details
                location_address: {
                    address: newLocation.location_address.address,
                    state: newLocation.location_address.state || '',
                    city: newLocation.location_address.city || '',
                    latitude: newLocation.location_address.latitude || null,
                    longitude: newLocation.location_address.longitude || null,
                    pincode: newLocation.location_address.pincode || ''
                },
                operating_hours: newLocation.operating_hours,
                vendor_location_type: newLocation.vendor_location_type,
                // Manager user details (will be created as VENDOR_MANAGER)
                manager_name: newLocation.manager_name,
                manager_mobile: newLocation.manager_mobile,
                manager_email: newLocation.manager_email || ''
            };

            // Get vendor ID from the vendor state
            if (!vendor || !vendor.id) {
                setError('Vendor information not found. Please refresh the page and try again.');
                return;
            }

            // Create vendor registration for new location
            const result = await vendorRegistrationService.createVendorRegistration({
                registration_type: 'new_record_existing_user',
                entity_type: 'vendor_location',
                user_mobile_number: userMobile,
                registration_data: registrationData,
                vendor_id: vendor.id, // Use the current vendor's ID
                entity_id: null
            });

            if (result.success) {
                // Now submit the registration for approval
                const registrationId = result.registration_id;
                if (registrationId) {
                    try {
                        const submitResult = await vendorRegistrationService.submitRegistration(registrationId);
                        
                        if (submitResult.success) {
                            // Show success message
                            alert('Location registration submitted successfully! It will be reviewed and approved by management.');
                        } else {
                            // Show warning that creation succeeded but submission failed
                            alert(`Location registration created but submission failed: ${submitResult.error || 'Unknown error'}. Please contact support.`);
                        }
                    } catch (submitError) {
                        // Show warning that creation succeeded but submission failed
                        alert('Location registration created but submission failed. Please contact support.');
                    }
                } else {
                    // Show warning that creation succeeded but no ID returned
                    alert('Location registration created but could not be submitted. Please contact support.');
                }

                // Reset form and refresh locations
                setNewLocation({
                    location_address: {
                        address: '',
                        city: '',
                        state: '',
                        pincode: '',
                        latitude: null,
                        longitude: null
                    },
                    operating_hours: {
                        monday: { open: '06:00', close: '18:00' },
                        tuesday: { open: '06:00', close: '18:00' },
                        wednesday: { open: '06:00', close: '18:00' },
                        thursday: { open: '06:00', close: '18:00' },
                        friday: { open: '06:00', close: '18:00' },
                        saturday: { open: '07:00', close: '16:00' },
                        sunday: { open: '08:00', close: '14:00' }
                    },
                    vendor_location_type: 'VENDOR_ONLY',
                    manager_name: '',
                    manager_mobile: '',
                    manager_email: ''
                });
                setShowAddLocationRow(false);
                
                // Refresh locations to show any existing approved locations
                fetchLocations();
            } else {
                setError(result.error || 'Failed to submit location registration');
            }
        } catch (err) {
            setError(err.message || 'Failed to submit location registration');
        } finally {
            setLoading(false);
        }
    }, [newLocation, user, vendor, fetchLocations]);

    const handleOperatingHoursChange = useCallback((day, field, value) => {
        setNewLocation(prev => ({
            ...prev,
            operating_hours: {
                ...prev.operating_hours,
                [day]: {
                    ...prev.operating_hours[day],
                    [field]: value
                }
            }
        }));
    }, []);

    const handleNewLocationChange = useCallback((field, value) => {
        if (field.includes('.')) {
            const [parent, child] = field.split('.');
            setNewLocation(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setNewLocation(prev => ({
                ...prev,
                [field]: value
            }));
        }
    }, []);

    const handleAddressSelect = useCallback((addressData) => {
        setNewLocation(prev => ({
            ...prev,
            location_address: {
                address: addressData.address,
                state: addressData.state,
                city: addressData.city,
                latitude: addressData.latitude,
                longitude: addressData.longitude,
                pincode: addressData.pincode
            }
        }));
    }, []);

    const handleAddressChange = useCallback((value) => {
        setNewLocation(prev => ({
            ...prev,
            location_address: {
                ...prev.location_address,
                address: value
            }
        }));
    }, []);



    const formatMobileNumber = useCallback((value) => {
        const digits = value.replace(/\D/g, '');
        return digits.slice(0, 10);
    }, []);

    const handleDeactivateClick = useCallback((location) => {
        setLocationToDeactivate(location);
        setShowDeactivateConfirm(true);
    }, []);

    const handleEditClick = useCallback((location) => {
        setEditingLocationId(location.id);
        
        // Expand the row if not already expanded
        if (!expandedRows.has(location.id)) {
            toggleRowExpansion(location.id);
        }
    }, [expandedRows, toggleRowExpansion]);

    const handleCancelEdit = useCallback(() => {
        setEditingLocationId(null);
        // Collapse the currently expanded row
        if (editingLocationId) {
            const newExpandedRows = new Set(expandedRows);
            newExpandedRows.delete(editingLocationId);
            setExpandedRows(newExpandedRows);
        }
    }, [editingLocationId, expandedRows, setExpandedRows]);

    const handleSaveEdit = useCallback(async (location, changedFields) => {
        try {
            setLoading(true);
            setError('');
            
            // Get current user mobile number
            const userMobile = user?.mobile_number;
            if (!userMobile) {
                setError('User mobile number not found. Please log in again.');
                return;
            }

            // Build registration data using only changed fields, merging with existing data
            const registrationData = {};
            
            // Handle location_address - if any address field changed, include the full address
            if (changedFields.location_address) {
                registrationData.location_address = {
                    address: changedFields.location_address.address || location.location_address?.address || '',
                    state: changedFields.location_address.state || location.location_address?.state || '',
                    city: changedFields.location_address.city || location.location_address?.city || '',
                    latitude: changedFields.location_address.latitude || location.location_address?.latitude || null,
                    longitude: changedFields.location_address.longitude || location.location_address?.longitude || null,
                    pincode: changedFields.location_address.pincode || location.location_address?.pincode || ''
                };
            }
            
            // Handle operating_hours - if any day changed, include all days
            if (changedFields.operating_hours) {
                registrationData.operating_hours = changedFields.operating_hours;
            }
            
            // Handle other fields
            if (changedFields.vendor_location_type !== undefined) {
                registrationData.vendor_location_type = changedFields.vendor_location_type;
            }
            
            if (changedFields.manager_name !== undefined) {
                registrationData.manager_name = changedFields.manager_name;
            }
            
            if (changedFields.manager_mobile !== undefined) {
                registrationData.manager_mobile = changedFields.manager_mobile;
            }
            
            if (changedFields.manager_email !== undefined) {
                registrationData.manager_email = changedFields.manager_email;
            }
            
            // Validate that we have at least some data to submit
            if (Object.keys(registrationData).length === 0) {
                setError('No valid changes to submit');
                return;
            }

            // Create vendor registration for location update
            const result = await vendorRegistrationService.createVendorRegistration({
                registration_type: 'existing_record_update',
                entity_type: 'vendor_location',
                user_mobile_number: userMobile,
                registration_data: registrationData,
                vendor_id: vendor.id,
                entity_id: location.id // Use the existing location ID
            });

            if (result.success) {
                // Now submit the registration for approval
                const registrationId = result.registration_id;
                if (registrationId) {
                    try {
                        const submitResult = await vendorRegistrationService.submitRegistration(registrationId);
                        
                        if (submitResult.success) {
                            // Show success message
                            alert('Location update submitted successfully! It will be reviewed and approved by management.');
                            
                            // Exit edit mode and collapse row
                            setEditingLocationId(null);
                            const newExpandedRows = new Set(expandedRows);
                            newExpandedRows.delete(location.id);
                            setExpandedRows(newExpandedRows);
                            
                            // Refresh locations
                            fetchLocations();
                        } else {
                            // Show warning that creation succeeded but submission failed
                            alert(`Location update created but submission failed: ${submitResult.error || 'Unknown error'}. Please contact support.`);
                        }
                    } catch (submitError) {
                        alert('Location update created but submission failed. Please contact support.');
                    }
                } else {
                    alert('Location update created but could not be submitted. Please contact support.');
                }
            } else {
                setError(result.error || 'Failed to submit location update');
            }
        } catch (err) {
            setError(err.message || 'Failed to submit location update');
        } finally {
            setLoading(false);
        }
    }, [user, vendor, fetchLocations, expandedRows, setExpandedRows]);

    const confirmDeactivate = useCallback(async () => {
        if (!locationToDeactivate) return;
        
        try {
            await handleStatusToggle(locationToDeactivate.id, locationToDeactivate.is_active);
            setShowDeactivateConfirm(false);
            setLocationToDeactivate(null);
        } catch (err) {
            // Error handling for deactivation
        }
    }, [locationToDeactivate, handleStatusToggle]);

    const getLocationTypeBadge = useCallback((type) => {
        const badges = {
            'VENDOR_ONLY': { color: 'bg-blue-100 text-blue-800', text: 'Vendor Only' },
            'VENDOR_AND_DEALER': { color: 'bg-green-100 text-green-800', text: 'Vendor & Dealer' },
            'DEALER_PARTNER': { color: 'bg-purple-100 text-purple-800', text: 'Dealer Partner' }
        };
        
        const badge = badges[type] || { color: 'bg-gray-100 text-gray-800', text: type };
        
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                {badge.text}
            </span>
        );
    }, []);

    const getStatusBadge = useCallback((isActive) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
            {isActive ? 'Active' : 'Inactive'}
        </span>
    ), []);

    const LocationRow = useCallback(({ location }) => {
        const isExpanded = expandedRows.has(location.id);
        
        return (
            <div className="bg-white border border-gray-200 rounded-lg mb-4 overflow-hidden">
                {/* Main Row - Clickable */}
                <div 
                    className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => toggleRowExpansion(location.id)}
                >
                    <div className="flex items-start md:items-center justify-between gap-3 flex-col md:flex-row">
                        <div className="flex items-start space-x-3 min-w-0 flex-1">
                            <div className="text-gray-500 mt-1 shrink-0">
                                {isExpanded ? (
                                    <ChevronDown className="w-5 h-5" />
                                ) : (
                                    <ChevronRight className="w-5 h-5" />
                                )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                                <h3 className="text-base md:text-lg font-semibold text-gray-900 break-words">
                                    {location.location_address?.address || 'Location Address'}
                                    {editingLocationId === location.id && (
                                        <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            <Edit className="w-3 h-3 mr-1" />
                                            Editing
                                        </span>
                                    )}
                                </h3>
                                <div className="text-sm text-gray-600 mt-1">
                                    {location.location_address?.city && `${location.location_address.city}, `}
                                    {location.location_address?.state && `${location.location_address.state}`}
                                    <span className="md:hidden"> · {location.driver_count || 0} drivers</span>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex items-center flex-wrap gap-2 pl-8 md:pl-0 md:space-x-3 shrink-0">
                            <div className="hidden md:block text-sm text-gray-500">
                                {location.driver_count || 0} drivers
                            </div>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditClick(location);
                                }}
                                className="px-3 py-1 text-xs font-medium rounded-md transition-colors bg-blue-100 text-blue-700 hover:bg-blue-200"
                            >
                                <Edit className="w-3 h-3 inline mr-1" />
                                Edit
                            </button>
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeactivateClick(location);
                                }}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                    location.is_active
                                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                                }`}
                            >
                                {location.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                    <>

                        {editingLocationId === location.id ? (
                            /* Edit Form */
                            <EditLocationRow
                                key={`edit-${location.id}`}
                                location={location}
                                onCancelEdit={handleCancelEdit}
                                onSaveEdit={(changedFields) => handleSaveEdit(location, changedFields)}
                                loading={loading}
                                formatMobileNumber={formatMobileNumber}
                            />
                        ) : (
                            /* Normal View */
                            <div className="border-t border-gray-200 bg-gray-50 p-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Address & Basic Info */}
                                    <div className="space-y-3">
                                        <div>
                                            <h4 className="font-medium text-gray-900 mb-2">Address Details</h4>
                                            <div className="bg-white p-3 rounded-md">
                                                <p className="text-gray-900">{location.location_address?.address}</p>
                                                {location.location_address?.city && (
                                                    <p className="text-gray-600 text-sm">{location.location_address.city}</p>
                                                )}
                                                {location.location_address?.latitude && location.location_address?.longitude && (
                                                    <p className="text-gray-500 text-xs mt-1">
                                                        Coordinates: {location.location_address.latitude}, {location.location_address.longitude}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {location.manager && (
                                            <div>
                                                <h4 className="font-medium text-gray-900 mb-2">Manager</h4>
                                                <div className="bg-white p-3 rounded-md">
                                                    <p className="text-gray-900">{location.manager.name}</p>
                                                    <p className="text-gray-600 text-sm">{location.manager.mobile_number ? location.manager.mobile_number.slice(-10) : 'Not set'}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Operating Hours */}
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-2">Operating Hours</h4>
                                        <div className="bg-white p-3 rounded-md">
                                            <div className="grid grid-cols-1 gap-2">
                                                {location.operating_hours && Object.entries(location.operating_hours).map(([day, hours]) => (
                                                    <div key={day} className="flex justify-between text-sm">
                                                        <span className="font-medium capitalize">{day}:</span>
                                                        <span className="text-gray-600">
                                                            {hours.open} - {hours.close}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Info */}
                                {location.dealer && (
                                    <div className="mt-4">
                                        <h4 className="font-medium text-gray-900 mb-2">Dealer Information</h4>
                                        <div className="bg-white p-3 rounded-md">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                <div>
                                                    <span className="font-medium">Name:</span> {location.dealer.dealer_name}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Code:</span> {location.dealer.dealer_code}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Type:</span> {location.dealer.dealer_type}
                                                </div>
                                                <div>
                                                    <span className="font-medium">Contact:</span> {location.dealer.contact_number}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="mt-4 text-xs text-gray-500">
                                    Created: {formatDate(location.created_at)}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    }, [
        expandedRows,
        toggleRowExpansion,
        handleDeactivateClick,
        editingLocationId,
        formatMobileNumber,
        handleCancelEdit,
        handleEditClick,
        handleSaveEdit,
        loading,
    ]);




    const LocationModal = ({ location, isOpen, onClose }) => {
        if (!isOpen || !location) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">Location Details</h2>
                                <p className="text-gray-600 mt-1">{location.location_address?.address}</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Content */}
                        <div className="space-y-6">
                            {/* Basic Info */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Basic Information</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Location Type</label>
                                        <div className="mt-1">{getLocationTypeBadge(location.vendor_location_type)}</div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Status</label>
                                        <div className="mt-1">{getStatusBadge(location.is_active)}</div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Drop-offs Accepted</label>
                                        <div className="mt-1">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                location.can_accept_drop_offs 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {location.can_accept_drop_offs ? 'Yes' : 'No'}
                                            </span>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Active Drivers</label>
                                        <div className="mt-1 text-lg font-semibold text-gray-900">{location.driver_count}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Address */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Address</h3>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-gray-900">{location.location_address?.address}</p>
                                    {location.location_address?.city && (
                                        <p className="text-gray-600 mt-1">{location.location_address.city}</p>
                                    )}
                                    {location.location_address?.latitude && location.location_address?.longitude && (
                                        <p className="text-gray-500 text-sm mt-2">
                                            Coordinates: {location.location_address.latitude}, {location.location_address.longitude}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Operating Hours */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Operating Hours</h3>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {location.operating_hours && Object.entries(location.operating_hours).map(([day, hours]) => (
                                            <div key={day} className="flex justify-between">
                                                <span className="font-medium capitalize">{day}:</span>
                                                <span className="text-gray-600">
                                                    {hours.open} - {hours.close}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Manager Info */}
                            {location.manager && (
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Manager</h3>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Name</label>
                                                <p className="mt-1 text-gray-900">{location.manager.name}</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Mobile</label>
                                                <p className="mt-1 text-gray-900">{location.manager.mobile_number ? location.manager.mobile_number.slice(-10) : 'Not set'}</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Username</label>
                                                <p className="mt-1 text-gray-900">{location.manager.username}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Dealer Info */}
                            {location.dealer && (
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Dealer Information</h3>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Dealer Name</label>
                                                <p className="mt-1 text-gray-900">{location.dealer.dealer_name}</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Dealer Code</label>
                                                <p className="mt-1 text-gray-900">{location.dealer.dealer_code}</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Type</label>
                                                <p className="mt-1 text-gray-900">{location.dealer.dealer_type}</p>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700">Contact</label>
                                                <p className="mt-1 text-gray-900">{location.dealer.contact_number}</p>
                                            </div>
                                        </div>
                                        {location.dealer.supported_brands && location.dealer.supported_brands.length > 0 && (
                                            <div className="mt-4">
                                                <label className="block text-sm font-medium text-gray-700">Supported Brands</label>
                                                <div className="mt-1 flex flex-wrap gap-2">
                                                    {location.dealer.supported_brands.map((brand, index) => (
                                                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                            {brand}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="page-container">
                <div className="content-container">
                    <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="content-container">
                {/* Header */}
                <div className="card mb-4 md:mb-6">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                                Locations Management
                            </h1>
                            <p className="text-gray-600 mt-1 text-sm md:text-base">
                                {vendor ? `Manage locations for ${vendor.vendor_name}` : 'Manage your vendor locations and operating areas'}
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-4">
                            {/* Filter */}
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-gray-700 shrink-0">Filter:</label>
                                <select
                                    value={filterActive === null ? '' : filterActive.toString()}
                                    onChange={(e) => setFilterActive(e.target.value === '' ? null : e.target.value === 'true')}
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">All Locations</option>
                                    <option value="true">Active Only</option>
                                    <option value="false">Inactive Only</option>
                                </select>
                            </div>
                            
                            {/* Refresh Button */}
                            <button
                                onClick={fetchLocations}
                                className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span>Refresh</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="card mb-6 bg-red-50 border-red-200">
                        <div className="flex items-center space-x-2 text-red-700">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    </div>
                )}

                {/* Locations List */}
                {locations.length > 0 ? (
                    <div className="space-y-4">
                        {locations.map((location) => (
                            <LocationRow key={location.id} location={location} />
                        ))}
                        
                        {/* Add Location Row - Now within the same spacing container */}
                        {showAddLocationRow && (
                            <AddLocationRow
                                showAddLocationRow={showAddLocationRow}
                                setShowAddLocationRow={setShowAddLocationRow}
                                newLocation={newLocation}
                                loading={loading}
                                handleNewLocationChange={handleNewLocationChange}
                                handleAddressChange={handleAddressChange}
                                handleAddressSelect={handleAddressSelect}
                                handleOperatingHoursChange={handleOperatingHoursChange}
                                handleAddLocation={handleAddLocation}
                                formatMobileNumber={formatMobileNumber}
                            />
                        )}
                    </div>
                ) : (
                    <div className="card">
                        <div className="text-center py-12">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100 mb-4">
                                <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">
                                No Locations Found
                            </h2>
                            <p className="text-gray-600 max-w-md mx-auto">
                                {filterActive !== null 
                                    ? `No ${filterActive ? 'active' : 'inactive'} locations found.`
                                    : 'No locations have been configured for your vendor account yet.'
                                }
                            </p>
                        </div>
                    </div>
                )}

                {/* Add Location Row - For when there are no existing locations */}
                {locations.length === 0 && showAddLocationRow && (
                    <div className="mt-4">
                        <AddLocationRow
                            showAddLocationRow={showAddLocationRow}
                            setShowAddLocationRow={setShowAddLocationRow}
                            newLocation={newLocation}
                            loading={loading}
                            handleNewLocationChange={handleNewLocationChange}
                            handleAddressChange={handleAddressChange}
                            handleAddressSelect={handleAddressSelect}
                            handleOperatingHoursChange={handleOperatingHoursChange}
                            handleAddLocation={handleAddLocation}
                            formatMobileNumber={formatMobileNumber}
                        />
                    </div>
                )}

                {/* Add Location Button - Below the rows */}
                {!showAddLocationRow && (
                    <div className="text-center py-6">
                        <button
                            onClick={() => setShowAddLocationRow(true)}
                            className="flex items-center space-x-2 px-4 sm:px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors mx-auto text-sm sm:text-base"
                        >
                            <Plus className="w-5 h-5" />
                            <span className="hidden sm:inline">Submit Location Registration</span>
                            <span className="sm:hidden">Add Location</span>
                        </button>
                    </div>
                )}

                {/* Location Modal */}
                <LocationModal
                    location={selectedLocation}
                    isOpen={showLocationModal}
                    onClose={() => {
                        setShowLocationModal(false);
                        setSelectedLocation(null);
                    }}
                />

                {/* Deactivate Confirmation Modal */}
                {showDeactivateConfirm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
                            <div className="p-6">
                                <div className="flex items-center mb-4">
                                    <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                                        <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                </div>
                                
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                                        Confirm Deactivation
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-6">
                                        Are you sure you want to deactivate the location at{' '}
                                        <span className="font-medium">
                                            {locationToDeactivate?.location_address?.address}
                                        </span>?
                                        <br />
                                        <span className="text-red-600">
                                            This action cannot be undone.
                                        </span>
                                    </p>
                                    
                                    <div className="flex justify-center space-x-3">
                                        <button
                                            onClick={() => {
                                                setShowDeactivateConfirm(false);
                                                setLocationToDeactivate(null);
                                            }}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={confirmDeactivate}
                                            className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                                        >
                                            Deactivate
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Locations;
